/**
 * Page d'accueil — landing one-page (cahier des charges 15/09/2026).
 * Parcours : COMPRENDRE → ÊTRE RASSURÉ → COMPRENDRE COMMENT ÇA MARCHE → AGIR.
 * Les sections conservées de l'ancien site (Abonnement, Cas d'usage,
 * Professionnels, Télécharger) sont placées après la FAQ, avant le CTA final.
 */
import React from 'react';
import {
  HERO, WHY, SITUATIONS, HOW, ALERT, EMERGENCY, MUTUAL_AID, VIDEO, COLLECTIVITES, DATA,
  RESPONSIBILITY, FAQ, FINAL_CTA, PRICING, PROS, USE_CASES, DOWNLOAD, ANDROID_INSTALL_URL, APP_URL,
} from './content';
import { Icon, LogoMark } from './icons';
import { Header, Footer } from './Layout';

/* ── Hero ─────────────────────────────────────────────────────────────── */
function HeroIllustration() {
  /* Proximité + géolocalisation + alerte : cercles de portée, personnes à
     proximité, bouclier CIVIK au centre. Décoratif. */
  return (
    <svg className="lp-hero-art" viewBox="0 0 420 420" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id="hg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2F6FE0" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#2F6FE0" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="210" cy="210" r="200" fill="url(#hg)" />
      <g fill="none" stroke="#FFFFFF" strokeOpacity="0.16" strokeWidth="1.5">
        <circle cx="210" cy="210" r="70" />
        <circle cx="210" cy="210" r="120" />
        <circle cx="210" cy="210" r="170" />
      </g>
      <circle className="lp-hero-pulse" cx="210" cy="210" r="70" fill="none" stroke="#22A559" strokeWidth="2" />
      {[[110, 130], [318, 118], [330, 262], [96, 292], [232, 350], [150, 76]].map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y})`} fill="#FFFFFF" fillOpacity={i % 2 ? 0.85 : 0.65}>
          <circle cx="0" cy="-6" r="7" />
          <path d="M-11 14c0-7 5-11 11-11s11 4 11 11v2h-22z" />
        </g>
      ))}
      <g transform="translate(160 160)" color="#FFFFFF">
        <circle cx="50" cy="50" r="52" fill="#00004D" stroke="#FFFFFF" strokeOpacity="0.25" strokeWidth="2" />
        <g transform="translate(18 18) scale(1)">
          <path d="M14 8h36l8 8v20L32 60 6 36V16l8-8z" fill="none" stroke="#FFFFFF" strokeWidth="6" strokeLinejoin="round" />
          <path d="M41.5 22.5a12.5 12.5 0 100 19" fill="none" stroke="#FFFFFF" strokeWidth="7" strokeLinecap="round" />
          <circle cx="45" cy="42" r="5" fill="#22A559" />
        </g>
      </g>
    </svg>
  );
}

function Hero() {
  return (
    <section className="lp-hero" aria-labelledby="hero-title">
      <div className="lp-container lp-hero-inner">
        <div className="lp-hero-text">
          <p className="lp-hero-brand"><LogoMark size={44} className="lp-hero-mark" title="Logo CIVIK" /><span>CIVIK</span></p>
          <h1 id="hero-title">{HERO.h1}</h1>
          <p className="lp-hero-sub">{HERO.sub}</p>
          <div className="lp-hero-ctas">
            <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-lg" data-track="cta_utiliser_civik" data-track-place="hero">{HERO.ctaPrimary}</a>
            <a href="#comment-ca-marche" className="lp-btn lp-btn-outline-light lp-btn-lg" data-track="cta_decouvrir" data-track-place="hero">{HERO.ctaSecondary}</a>
          </div>
          <ul className="lp-hero-markers" aria-label="Points clés">
            {HERO.markers.map((m, i) => (
              <li key={m}><Icon name={['hands', 'pin', 'bell', 'lock'][i]} size={20} /><span>{m}</span></li>
            ))}
          </ul>
        </div>
        <div className="lp-hero-visual"><HeroIllustration /></div>
      </div>
    </section>
  );
}

/* ── Pourquoi CIVIK ───────────────────────────────────────────────────── */
function Why() {
  return (
    <section className="lp-section" aria-labelledby="why-title">
      <div className="lp-container lp-why">
        <div className="reveal">
          <p className="lp-kicker">Pourquoi CIVIK ?</p>
          <h2 id="why-title">{WHY.title}</h2>
        </div>
        <div className="lp-why-body reveal">
          <p>{WHY.p1}</p>
          <p>{WHY.p2}</p>
          <p className="lp-highlight"><Icon name="shield" size={22} /><strong>{WHY.highlight}</strong></p>
        </div>
      </div>
    </section>
  );
}

/* ── Situations d'usage ───────────────────────────────────────────────── */
function Situations() {
  return (
    <section className="lp-section lp-section-alt" aria-labelledby="sit-title">
      <div className="lp-container">
        <h2 id="sit-title" className="lp-center reveal">{SITUATIONS.title}</h2>
        <ul className="lp-cards lp-cards-4">
          {SITUATIONS.cards.map((c) => (
            <li key={c.title} className="lp-card reveal">
              <span className="lp-card-icon"><Icon name={c.icon} size={26} /></span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ── Comment ça marche ────────────────────────────────────────────────── */
function How() {
  return (
    <section id={HOW.id} data-section="comment-ca-marche" className="lp-section" aria-labelledby="how-title">
      <div className="lp-container">
        <div className="lp-section-head reveal">
          <p className="lp-kicker">Comment ça marche ?</p>
          <h2 id="how-title">{HOW.title}</h2>
        </div>
        <ol className="lp-steps">
          {HOW.steps.map((s) => (
            <li key={s.num} className="reveal">
              <span className="lp-step-num" aria-hidden="true">{s.num}</span>
              <h3>{s.label}</h3>
              <p>{s.text}</p>
            </li>
          ))}
        </ol>
        <p className="lp-note reveal"><Icon name="alert" size={22} /><span>{HOW.note}</span></p>
        <p className="lp-more reveal"><a href="/comment-ca-marche" data-track="page_comment_ca_marche">Comment ça marche, en détail</a></p>
      </div>
    </section>
  );
}

/* ── Alerte / danger ──────────────────────────────────────────────────── */
function Alert() {
  return (
    <section id={ALERT.id} data-section="securite" className="lp-section lp-alert" aria-labelledby="alert-title">
      <div className="lp-container">
        <div className="lp-section-head reveal">
          <p className="lp-kicker lp-kicker-alert">Sécurité</p>
          <h2 id="alert-title">{ALERT.title}</h2>
          <p className="lp-lead">{ALERT.text}</p>
        </div>
        <ol className="lp-timeline">
          {ALERT.steps.map((s, i) => (
            <li key={s} className="reveal"><span className="lp-timeline-num" aria-hidden="true">{i + 1}</span><span>{s}</span></li>
          ))}
        </ol>
        <p className="lp-disclaimer reveal" role="note">
          <Icon name="shield" size={26} />
          <strong>{ALERT.disclaimer}</strong>
        </p>
        <p className="lp-more lp-more-dark reveal"><a href="/securite-citoyenne" data-track="page_securite">Sécurité citoyenne : en savoir plus</a></p>
      </div>
    </section>
  );
}

/* ── Services d'urgence ───────────────────────────────────────────────── */
function Emergency() {
  return (
    <section className="lp-section lp-emergency" aria-labelledby="emg-title">
      <div className="lp-container">
        <h2 id="emg-title" className="lp-center reveal">{EMERGENCY.title}</h2>
        <ul className="lp-numbers">
          {EMERGENCY.numbers.map((n) => (
            <li key={n.number} className={`lp-number lp-number-${n.number} reveal`}>
              <a href={n.tel}>
                <span className="lp-number-value">{n.number}</span>
                <span className="lp-number-label">{n.label}</span>
              </a>
            </li>
          ))}
        </ul>
        <p className="lp-center lp-emergency-text reveal">{EMERGENCY.text}</p>
      </div>
    </section>
  );
}

/* ── Entraide ─────────────────────────────────────────────────────────── */
function MutualAid() {
  return (
    <section id={MUTUAL_AID.id} className="lp-section lp-section-alt" aria-labelledby="aid-title">
      <div className="lp-container">
        <div className="lp-section-head reveal">
          <p className="lp-kicker">Entraide</p>
          <h2 id="aid-title">{MUTUAL_AID.title}</h2>
          <p className="lp-lead">{MUTUAL_AID.p1}</p>
          <p className="lp-lead">{MUTUAL_AID.p2}</p>
        </div>
        <ul className="lp-principles">
          {MUTUAL_AID.principles.map((p) => (
            <li key={p.title} className="reveal">
              <span className="lp-principle-icon"><Icon name={p.icon} size={28} /></span>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </li>
          ))}
        </ul>
        <p className="lp-more reveal"><a href="/entraide-citoyenne" data-track="page_entraide">L'entraide citoyenne avec CIVIK : en savoir plus</a></p>
      </div>
    </section>
  );
}

/* ── Vidéo et documentation ───────────────────────────────────────────── */
function Video() {
  return (
    <section className="lp-section" aria-labelledby="video-title">
      <div className="lp-container lp-split">
        <div className="lp-split-visual reveal" aria-hidden="true">
          <div className="lp-cam-frame">
            <Icon name="camera" size={56} />
            <span className="lp-cam-rec">Documentation</span>
          </div>
        </div>
        <div className="lp-split-text reveal">
          <p className="lp-kicker">Vidéo et documentation</p>
          <h2 id="video-title">{VIDEO.title}</h2>
          <p>{VIDEO.p1}</p>
          <p>{VIDEO.p2}</p>
          <p>{VIDEO.p3}</p>
          <a href={VIDEO.ctaHref} className="lp-btn lp-btn-secondary" data-track="page_donnees" data-track-place="section-video">{VIDEO.cta}</a>
        </div>
      </div>
    </section>
  );
}

/* ── Collectivités ────────────────────────────────────────────────────── */
export function CollectivitesBlock({ asSection = true, showCtas = true }) {
  const Tag = asSection ? 'section' : 'div';
  return (
    <Tag id={asSection ? COLLECTIVITES.id : undefined} data-section={asSection ? 'collectivites' : undefined} className={asSection ? 'lp-section lp-collectivites' : 'lp-collectivites-inline'} aria-labelledby={asSection ? 'coll-title' : undefined}>
      <div className="lp-container">
        <div className="lp-section-head reveal">
          <p className="lp-kicker lp-kicker-inst"><Icon name="building" size={18} /> Collectivités</p>
          {asSection ? <h2 id="coll-title">{COLLECTIVITES.title}</h2> : <h2>{COLLECTIVITES.subtitle}</h2>}
          {asSection ? <p className="lp-lead lp-lead-strong">{COLLECTIVITES.subtitle}</p> : null}
          <p className="lp-lead">{COLLECTIVITES.text}</p>
        </div>
        <ul className="lp-benefits">
          {COLLECTIVITES.benefits.map((b) => (
            <li key={b.title} className="reveal">
              <span className="lp-benefit-icon"><Icon name={b.icon} size={26} /></span>
              <div>
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </div>
            </li>
          ))}
        </ul>
        {showCtas ? (
          <div className="lp-ctas reveal">
            <a href={COLLECTIVITES.ctaPrimaryHref} className="lp-btn lp-btn-inst lp-btn-lg" data-track="cta_collectivite" data-track-place="section">{COLLECTIVITES.ctaPrimary}</a>
            <a href={COLLECTIVITES.ctaSecondaryHref} className="lp-btn lp-btn-outline lp-btn-lg" data-track="cta_contact" data-track-place="section">{COLLECTIVITES.ctaSecondary}</a>
          </div>
        ) : null}
        {showCtas ? (
          <p className="lp-more reveal"><a href="/police-municipale" data-track="page_police_municipale">CIVIK et la police municipale</a></p>
        ) : null}
      </div>
    </Tag>
  );
}

/* ── Données & confidentialité ────────────────────────────────────────── */
export function DataBlock({ asSection = true }) {
  const Tag = asSection ? 'section' : 'div';
  return (
    <Tag id={asSection ? DATA.id : undefined} data-section={asSection ? 'donnees-confidentialite' : undefined} className={asSection ? 'lp-section lp-data' : 'lp-data-inline'} aria-labelledby={asSection ? 'data-title' : undefined}>
      <div className="lp-container">
        <div className="lp-section-head reveal">
          <p className="lp-kicker">Données & confidentialité</p>
          {asSection ? <h2 id="data-title">{DATA.title}</h2> : <h2>{DATA.title}</h2>}
          <p className="lp-lead">{DATA.p1}</p>
          <p className="lp-lead">{DATA.p2}</p>
        </div>
        <ul className="lp-cards lp-cards-4 lp-cards-data">
          {DATA.commitments.map((c) => (
            <li key={c.title} className="lp-card reveal">
              <span className="lp-card-icon"><Icon name={c.icon} size={26} /></span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </li>
          ))}
        </ul>
        {asSection ? (
          <div className="lp-ctas reveal"><a href={DATA.ctaHref} className="lp-btn lp-btn-secondary lp-btn-lg" data-track="page_donnees" data-track-place="section-donnees">{DATA.cta}</a></div>
        ) : null}
      </div>
    </Tag>
  );
}

/* ── Principe de responsabilité ───────────────────────────────────────── */
export function Responsibility() {
  return (
    <section className="lp-section lp-responsibility" aria-labelledby="resp-title">
      <div className="lp-container">
        <div className="lp-resp-box reveal">
          <span className="lp-resp-icon"><Icon name="check" size={34} /></span>
          <div>
            <h2 id="resp-title">{RESPONSIBILITY.title}</h2>
            <p className="lp-resp-main">{RESPONSIBILITY.main}</p>
            <p>{RESPONSIBILITY.text}</p>
            <p className="lp-resp-strong">{RESPONSIBILITY.strong}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── FAQ ──────────────────────────────────────────────────────────────── */
export function FaqList({ headingId }) {
  return (
    <div className="lp-faq-list">
      {FAQ.items.map((it, i) => (
        <details key={it.q} className="lp-faq-item reveal" data-track="faq_open" data-track-label={it.q}>
          <summary>
            <span className="lp-faq-q" id={`${headingId || 'faq'}-q${i + 1}`}>{it.q}</span>
            <Icon name="chevron" size={22} className="lp-faq-chevron" />
          </summary>
          <div className="lp-faq-a"><p>{it.a}</p>{it.a2 ? <p>{it.a2}</p> : null}</div>
        </details>
      ))}
    </div>
  );
}

function Faq() {
  return (
    <section id={FAQ.id} data-section="faq" className="lp-section lp-section-alt" aria-labelledby="faq-title">
      <div className="lp-container lp-narrow">
        <h2 id="faq-title" className="lp-center reveal">{FAQ.title}</h2>
        <FaqList headingId="faq" />
        <p className="lp-center lp-more reveal"><a href="/faq">Toutes les questions fréquentes</a></p>
      </div>
    </section>
  );
}

/* ── Sections conservées de l'ancien site ─────────────────────────────── */
function Pricing() {
  return (
    <section id={PRICING.id} className="lp-section" aria-labelledby="pricing-title">
      <div className="lp-container">
        <div className="lp-section-head lp-center reveal">
          <h2 id="pricing-title">{PRICING.title}</h2>
          <p className="lp-lead">{PRICING.sub}</p>
        </div>
        <div className="lp-pricing">
          {[PRICING.free, PRICING.paid].map((plan) => (
            <div key={plan.name} className={`lp-plan reveal${plan.badge ? ' lp-plan-featured' : ''}`}>
              {plan.badge ? <span className="lp-plan-badge">{plan.badge}</span> : null}
              <h3>{plan.name}</h3>
              <p className="lp-plan-price">{plan.price}<span>{plan.period}</span></p>
              <ul>{plan.items.map((it) => <li key={it}><Icon name="check" size={18} /><span>{it}</span></li>)}</ul>
              <a href={plan.href} className={`lp-btn ${plan.badge ? 'lp-btn-primary' : 'lp-btn-outline'} lp-btn-block`}>{plan.cta}</a>
            </div>
          ))}
        </div>
        <p className="lp-center lp-small reveal">{PRICING.note}</p>
      </div>
    </section>
  );
}

function UseCases() {
  return (
    <section id={USE_CASES.id} className="lp-section lp-section-alt" aria-labelledby="cases-title">
      <div className="lp-container">
        <h2 id="cases-title" className="lp-center reveal">{USE_CASES.title}</h2>
        <ul className="lp-cards lp-cards-5">
          {USE_CASES.cases.map((c) => (
            <li key={c.title} className="lp-card lp-card-compact reveal">
              <span className="lp-card-icon"><Icon name={c.icon} size={24} /></span>
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Professionals() {
  return (
    <section id={PROS.id} className="lp-section lp-pros" aria-labelledby="pros-title" data-section="professionnels">
      <div className="lp-container">
        <div className="lp-section-head lp-center reveal">
          <h2 id="pros-title">{PROS.title}</h2>
          <p className="lp-subtitle">{PROS.sub}</p>
          <p className="lp-lead">{PROS.intro}</p>
          <p className="lp-ctas lp-ctas-center">
            <a href={PROS.ctaHref} className="lp-btn lp-btn-primary lp-btn-lg" data-track="cta_candidater" data-track-place="professionnels">{PROS.cta}</a>
          </p>
        </div>

        <h3 className="lp-h3-center reveal">Votre parcours de candidature en cinq étapes</h3>
        <ol className="lp-pros-steps" aria-label="Parcours de candidature professionnelle">
          {PROS.steps.map((st) => (
            <li key={st.num} className="reveal">
              <span className="lp-pros-step-num" aria-hidden="true">{st.num}</span>
              <h4>{st.title}</h4>
              <p>{st.text}</p>
            </li>
          ))}
        </ol>

        <ul className="lp-cards lp-cards-2 lp-pros-profiles">
          {PROS.profiles.map((pr) => (
            <li key={pr.key} className="lp-card reveal">
              <span className="lp-card-icon"><Icon name={pr.icon} size={26} /></span>
              <h3>{pr.title}</h3>
              <p>{pr.text}</p>
              <p className="lp-card-cta"><a href={pr.href} className="lp-btn lp-btn-inst" data-track="cta_candidater" data-track-place={`profil-${pr.key}`}>{pr.cta}</a></p>
            </li>
          ))}
        </ul>

        <div className="lp-pros-network reveal">
          <span className="lp-pros-network-icon"><Icon name="network" size={28} /></span>
          <div>
            <h3>{PROS.network.title}</h3>
            <p>{PROS.network.p1}</p>
            <p>{PROS.network.p2}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Download() {
  return (
    <section id={DOWNLOAD.id} className="lp-section lp-download" aria-labelledby="dl-title">
      <div className="lp-container lp-narrow lp-center">
        <h2 id="dl-title" className="reveal">{DOWNLOAD.title}</h2>
        <p className="lp-lead reveal">{DOWNLOAD.sub}</p>
        <div className="lp-qr-block reveal">
          <p className="lp-qr-title">{DOWNLOAD.qrTitle}</p>
          <a href={ANDROID_INSTALL_URL} className="lp-qr-link" rel="noopener">
            <img src="/qr-android-civik.png" alt="QR code d'installation de l'application Android CIVIK" width="200" height="200" loading="lazy" decoding="async" />
          </a>
          <p className="lp-qr-hint">{DOWNLOAD.qrHint}</p>
          <a href={ANDROID_INSTALL_URL} className="lp-btn lp-btn-primary" rel="noopener">{DOWNLOAD.qrBtn}</a>
          <p className="lp-small">{DOWNLOAD.note}</p>
        </div>
        <div className="lp-store-row reveal">
          <span className="lp-store-btn" aria-disabled="true" title="Publication en cours de validation">{DOWNLOAD.appStore}</span>
          <span className="lp-store-btn" aria-disabled="true" title="Publication en cours de validation">{DOWNLOAD.playStore}</span>
          <a href={APP_URL} className="lp-store-btn lp-store-btn-active">{DOWNLOAD.webapp}</a>
        </div>
      </div>
    </section>
  );
}

/* ── CTA final ────────────────────────────────────────────────────────── */
function FinalCta() {
  return (
    <section className="lp-section lp-final" aria-labelledby="final-title">
      <div className="lp-container lp-center">
        <LogoMark size={56} className="lp-final-mark" />
        <h2 id="final-title" className="reveal">{FINAL_CTA.title}</h2>
        <p className="lp-lead reveal">{FINAL_CTA.text}</p>
        <div className="lp-ctas lp-ctas-center reveal">
          <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-lg" data-track="cta_utiliser_civik" data-track-place="final">{FINAL_CTA.ctaPrimary}</a>
          <a href={FINAL_CTA.ctaSecondaryHref} className="lp-btn lp-btn-outline-light lp-btn-lg" data-track="cta_collectivite" data-track-place="final">{FINAL_CTA.ctaSecondary}</a>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Header route="/" />
      <main id="contenu">
        <Hero />
        <Why />
        <Situations />
        <How />
        <Alert />
        <Emergency />
        <MutualAid />
        <Video />
        <CollectivitesBlock />
        <DataBlock />
        <Responsibility />
        <Faq />
        <Pricing />
        <UseCases />
        <Professionals />
        <Download />
        <FinalCta />
      </main>
      <Footer />
    </>
  );
}
